import { db } from '@/db/schema';
import { updateState } from '@/db/repositories/localSignState';

export async function setLearned(signId: string, learned: boolean): Promise<void> {
  await updateState(signId, (current) => ({ ...current, learned }));
}

export async function toggleLearned(signId: string): Promise<boolean> {
  const next = await updateState(signId, (current) => ({ ...current, learned: !current.learned }));
  return next.learned;
}

/**
 * Registra o resultado de uma rodada de estudo.
 *
 * `studyScore` é um contador simples e limitado: sobe no acerto, desce no erro, e
 * fica entre -3 e 5. Serve para priorizar o que o usuário erra sem prometer uma
 * repetição espaçada de verdade — isso é explicitamente pós-MVP (PLAN §22).
 */
export async function recordStudyResult(
  signId: string,
  correct: boolean,
  at: Date = new Date(),
): Promise<void> {
  await updateState(signId, (current) => ({
    ...current,
    correctCount: current.correctCount + (correct ? 1 : 0),
    incorrectCount: current.incorrectCount + (correct ? 0 : 1),
    studyScore: Math.max(-3, Math.min(5, current.studyScore + (correct ? 1 : -1))),
    lastStudiedAt: at.toISOString(),
  }));
}

export interface ProgressSummary {
  total: number;
  learned: number;
  /** 0..1 */
  ratio: number;
}

export async function categoryProgress(categoryId: string): Promise<ProgressSummary> {
  const signIds = await db.signs.where('categoryId').equals(categoryId).primaryKeys();
  if (signIds.length === 0) return { total: 0, learned: 0, ratio: 0 };

  const states = await db.localSignState.bulkGet(signIds);
  const learned = states.filter((state) => state?.learned).length;

  return { total: signIds.length, learned, ratio: learned / signIds.length };
}

export async function overallProgress(): Promise<ProgressSummary> {
  // Varredura filtrada: booleano não é indexável no IndexedDB (ver schema.ts).
  const total = await db.signs.filter((sign) => sign.active).count();
  if (total === 0) return { total: 0, learned: 0, ratio: 0 };

  const learned = await db.localSignState.filter((state) => state.learned).count();
  return { total, learned, ratio: learned / total };
}
